import { useRef, useState, type ReactNode } from 'react';
import { ScrollToTop } from '../ScrollToTop/ScrollToTop';

export interface AppLayoutApi {
  /** Toggle the mobile sidebar (wire to the navbar hamburger). */
  toggleSidebar: () => void;
  /** Close the mobile sidebar (wire to the sidebar's close button / nav clicks). */
  closeSidebar: () => void;
  /** Whether the mobile sidebar is currently expanded. */
  sidebarExpanded: boolean;
  /** Toggle the secondary mobile sidebar (wire to the navbar's secondary toggle). */
  toggleSecondarySidebar: () => void;
  /** Close the secondary mobile sidebar (wire to its close button / item clicks). */
  closeSecondarySidebar: () => void;
  /** Whether the secondary mobile sidebar is currently expanded. */
  secondarySidebarExpanded: boolean;
  /** Whether the main sidebar is collapsed to an icon-only rail (desktop). */
  sidebarResized: boolean;
  /** Toggle the main sidebar between full and icon-only (desktop); persisted in localStorage. */
  toggleSidebarResize: () => void;
}

const RESIZE_KEY = 'jordan.sidebarResized';

export interface AppLayoutProps {
  /** Render the top navbar; receives the layout API (for the mobile toggle). */
  navbar: (api: AppLayoutApi) => ReactNode;
  /** Render the sidebar; pass `mobileExpanded={api.sidebarExpanded}` to <Sidebar/>. */
  sidebar: (api: AppLayoutApi) => ReactNode;
  /**
   * Optional second sidebar rendered alongside the main one (same side). Pass
   * `mobileExpanded={api.secondarySidebarExpanded}` and `onClose={api.closeSecondarySidebar}`
   * to <SecondarySidebar/>.
   */
  secondarySidebar?: (api: AppLayoutApi) => ReactNode;
  /** Page content — typically a <PageHeader/> followed by a <div className="content">. */
  children: ReactNode;
  /** Optional footer rendered at the bottom of the scrolling content (e.g. <Footer/>). */
  footer?: ReactNode;
}

/**
 * Limitless layout_1 shell with a FIXED header + sidebar: the navbar and sidebar stay put
 * while only the content area scrolls. Includes an optional footer and a back-to-top button.
 */
export function AppLayout({ navbar, sidebar, secondarySidebar, children, footer }: AppLayoutProps) {
  const [sidebarExpanded, setExpanded] = useState(false);
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);
  const [sidebarResized, setResized] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(RESIZE_KEY) === '1',
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const api: AppLayoutApi = {
    sidebarExpanded,
    toggleSidebar: () => setExpanded((v) => !v),
    closeSidebar: () => setExpanded(false),
    secondarySidebarExpanded: secondaryExpanded,
    toggleSecondarySidebar: () => setSecondaryExpanded((v) => !v),
    closeSecondarySidebar: () => setSecondaryExpanded(false),
    sidebarResized,
    toggleSidebarResize: () =>
      setResized((v) => {
        const next = !v;
        if (typeof window !== 'undefined') window.localStorage.setItem(RESIZE_KEY, next ? '1' : '0');
        return next;
      }),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {navbar(api)}
      <div className="page-content" style={{ flex: 1, minHeight: 0 }}>
        {sidebar(api)}
        {secondarySidebar?.(api)}
        <div className="content-wrapper" ref={contentRef} style={{ overflowY: 'auto', minHeight: 0 }}>
          <div className="content-inner d-flex flex-column" style={{ minHeight: '100%' }}>
            <div className="flex-grow-1">{children}</div>
            {footer}
          </div>
        </div>
      </div>
      <ScrollToTop scrollRef={contentRef} />
    </div>
  );
}
